/**
 * UID-330 — map an uploaded file to the client-side renderer that can produce a
 * thumbnail for it, or `null` when we cannot render it (the graceful placeholder
 * stays). The single source of truth for "which uploads get a real thumbnail",
 * derived from the SAME accepted-extension list the Dropzone enforces
 * (`ACCEPTED_CAD_EXTENSIONS` in `../types.ts`) so the two can never drift.
 *
 * Coverage (see the UID-330 plan):
 *   - `.stl`                       → 'stl'  (three.js STLLoader)
 *   - `.step/.stp/.iges/.igs`      → 'cad'  (occt-import-js OpenCASCADE WASM → three.js)
 *   - `.pdf`, `.x_t` (Parasolid), `.sldprt` (SolidWorks), `.csv` → null
 *
 * `.pdf` is deliberately UNRENDERED here, not deferred to a licensed-converter
 * gap like `.x_t`/`.sldprt`: nothing in this pipeline yet distinguishes "this PDF
 * IS the part" (a standalone row) from "this PDF is a part's linked drawing/spec
 * sheet" (2026-09-04 review). Rendering page 1 unconditionally would put a
 * rasterized spec-sheet screenshot in the same slot a real part photo occupies,
 * which reads as "this is what the part looks like" — a misrepresentation risk.
 * Re-enable once UID-335's drawing-linking/preview work can route a PDF's
 * thumbnail to a dedicated drawing-preview slot instead of the generic
 * part-image one.
 */

export type ThumbnailRenderKind = 'stl' | 'cad'

/** Extension (lower-case, incl. leading dot) → renderer kind. Anything absent
 * here is deliberately unrendered (returns `null` from {@link kindForFile}). */
const EXTENSION_KIND: Readonly<Record<string, ThumbnailRenderKind>> = {
  '.stl': 'stl',
  '.step': 'cad',
  '.stp': 'cad',
  '.iges': 'cad',
  '.igs': 'cad',
}

/** Lower-cased extension INCLUDING the dot (`"a.b.STEP"` → `".step"`), or `""`
 * when the name has no extension. Only the final segment matters. */
export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.')
  if (dot < 0 || dot === filename.length - 1) {
    return ''
  }
  return filename.slice(dot).toLowerCase()
}

/**
 * The renderer kind for a file, or `null` if we cannot produce a thumbnail for
 * it. Classified by extension (content-type is unreliable for CAD formats — most
 * upload as `application/octet-stream`). `null` is a first-class, expected result:
 * the caller simply skips rendering and the placeholder remains.
 */
export function kindForFile(file: Pick<File, 'name'>): ThumbnailRenderKind | null {
  return EXTENSION_KIND[extensionOf(file.name)] ?? null
}
